import { useCallback } from 'react';

import { PractitionerView } from '@dreryk/egabinet-personnel-frontend-api';

import {
   InteractiveTable,
   InteractiveTableSelectorItemFilter,
   InteractiveTableSelectorItemKeyExtractor,
   InteractiveTableSelectorItemRenderer,
   InteractiveTableSelectorMapper,
   InteractiveTableSelectorTableKeyExtractor,
} from '@dreryk/react-components';
import { FormContextValues } from '@dreryk/react-common';

import { PractitionerSafePresenter } from '~libs.desktop.shared.practitioner-safe-presenter';
import { compact } from '~libs.typescript';

import {
   EmergencyNotificationInAppConfigurationResponseTeamPractitionersFieldFormBeanPart,
   EmergencyNotificationInAppConfigurationResponseTeamPractitionerValue,
} from '../EmergencyNotificationInAppConfigurationResponseTeamPractitionersField.types';
import { translationKeys } from '../../../../translations';

type EmergencyNotificationInAppConfigurationResponseTeamPractitionersInteractiveTableSelectorProps = Pick<
   FormContextValues<EmergencyNotificationInAppConfigurationResponseTeamPractitionersFieldFormBeanPart>,
   'register'
> & {
   readonly practitioners: PractitionerView[];
};

// eslint-disable-next-line max-lines-per-function
export const EmergencyNotificationInAppConfigurationResponseTeamPractitionersInteractiveTableSelector = (
   props: EmergencyNotificationInAppConfigurationResponseTeamPractitionersInteractiveTableSelectorProps
): JSX.Element => {
   const mapper = useCallback<
      InteractiveTableSelectorMapper<
         PractitionerView,
         EmergencyNotificationInAppConfigurationResponseTeamPractitionerValue
      >
   >((practitioner) => {
      return {
         practitioner: practitioner,
      };
   }, []);

   const itemRenderer = useCallback<InteractiveTableSelectorItemRenderer<PractitionerView>>(
      (practitioner, Highlight) => {
         return (
            <Highlight
               node={
                  <PractitionerSafePresenter
                     practitioner={practitioner}
                     testId={`practitioner-${practitioner.id}`}
                     type={'text'}
                     withIdentification
                  />
               }
            />
         );
      },
      []
   );

   const itemKeyExtractor = useCallback<InteractiveTableSelectorItemKeyExtractor<PractitionerView>>((practitioner) => {
      return practitioner.id.toString();
   }, []);

   const tableKeyExtractor = useCallback<
      InteractiveTableSelectorTableKeyExtractor<EmergencyNotificationInAppConfigurationResponseTeamPractitionerValue>
   >((value) => {
      return value.practitioner.id.toString();
   }, []);

   const itemFilter = useCallback<InteractiveTableSelectorItemFilter<PractitionerView>>((item) => {
      return compact([
         item.firstName.asString(),
         item.lastName.asString(),
         item.medicalLicenceNumber?.getValue() ?? item.pesel?.asString(),
      ]);
   }, []);

   return (
      <InteractiveTable.Selector<
         PractitionerView,
         EmergencyNotificationInAppConfigurationResponseTeamPractitionerValue,
         EmergencyNotificationInAppConfigurationResponseTeamPractitionersFieldFormBeanPart
      >
         register={props.register}
         testId={'responseTeamPractitionersSelector'}
         name={'responseTeamPractitioners.selector'}
         label={translationKeys.chooseResponseTeamPractitioners}
         placeholder={translationKeys.usersDeclensionMap}
         items={props.practitioners}
         itemKeyExtractor={itemKeyExtractor}
         tableKeyExtractor={tableKeyExtractor}
         itemRenderer={itemRenderer}
         itemFilter={itemFilter}
         mapper={mapper}
         required
      />
   );
};
